import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SERVICES } from "@/lib/siteData";
import { Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  device_type: "",
  service: "",
  issue: "",
  consent: false,
};

export default function ContactForm() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your full name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (form.phone.trim().length < 5) e.phone = "Please enter a contact phone.";
    if (!form.device_type) e.device_type = "Please select a device.";
    if (!form.service) e.service = "Please select a service.";
    if (form.issue.trim().length < 5) e.issue = "Please describe the issue briefly.";
    if (!form.consent) e.consent = "Consent is required to contact you.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Message sent.", {
        description: "A technician will contact you within 1 business hour.",
      });
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send. Please call us directly.";
      toast.error("Submission failed", { description: String(msg) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      data-testid="contact-form"
      onSubmit={onSubmit}
      className="bg-card border border-border rounded-md p-6 md:p-8 space-y-5"
      noValidate
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-1.5">
          <Label htmlFor="name" className="text-sm font-medium">Full name</Label>
          <Input
            id="name"
            data-testid="contact-name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Jane Doe"
            aria-invalid={!!errors.name}
          />
          {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-sm font-medium">Email</Label>
          <Input
            id="email"
            data-testid="contact-email"
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="jane@example.com"
            aria-invalid={!!errors.email}
          />
          {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-1.5">
          <Label htmlFor="phone" className="text-sm font-medium">Phone</Label>
          <Input
            id="phone"
            data-testid="contact-phone"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="+1 (555) 123-4567"
            aria-invalid={!!errors.phone}
          />
          {errors.phone && <p className="text-xs text-destructive">{errors.phone}</p>}
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Device</Label>
          <Select value={form.device_type} onValueChange={(v) => update("device_type", v)}>
            <SelectTrigger data-testid="contact-device" aria-invalid={!!errors.device_type}>
              <SelectValue placeholder="Select a device type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Windows PC">Windows PC / Laptop</SelectItem>
              <SelectItem value="Mac">Mac / MacBook / iMac</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
          {errors.device_type && <p className="text-xs text-destructive">{errors.device_type}</p>}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-sm font-medium">Service needed</Label>
        <Select value={form.service} onValueChange={(v) => update("service", v)}>
          <SelectTrigger data-testid="contact-service" aria-invalid={!!errors.service}>
            <SelectValue placeholder="Select a service" />
          </SelectTrigger>
          <SelectContent>
            {SERVICES.map((s) => (
              <SelectItem key={s.key} value={s.title}>{s.title} — {s.priceLabel}</SelectItem>
            ))}
            <SelectItem value="Not sure">I&rsquo;m not sure — please advise</SelectItem>
          </SelectContent>
        </Select>
        {errors.service && <p className="text-xs text-destructive">{errors.service}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="issue" className="text-sm font-medium">Describe the issue</Label>
        <Textarea
          id="issue"
          data-testid="contact-issue"
          rows={4}
          value={form.issue}
          onChange={(e) => update("issue", e.target.value)}
          placeholder="My MacBook freezes during startup and shows a spinning wheel..."
          aria-invalid={!!errors.issue}
        />
        {errors.issue && <p className="text-xs text-destructive">{errors.issue}</p>}
      </div>

      <div className="flex items-start gap-3">
        <Checkbox
          id="consent"
          data-testid="contact-consent"
          checked={form.consent}
          onCheckedChange={(v) => update("consent", Boolean(v))}
        />
        <Label htmlFor="consent" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
          I agree to be contacted about this enquiry and have read the
          {" "}<a href="/privacy" className="text-primary underline">Privacy Policy</a>. I understand Repair Point
          is an independent service and is not affiliated with Microsoft or Apple.
        </Label>
      </div>
      {errors.consent && <p className="text-xs text-destructive -mt-3">{errors.consent}</p>}

      <Button
        type="submit"
        disabled={submitting}
        data-testid="contact-submit"
        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-11 font-semibold"
      >
        {submitting ? (
          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
        ) : (
          <><Send className="h-4 w-4 mr-2" /> Request a written quote</>
        )}
      </Button>

      <p className="text-[11px] text-muted-foreground text-center">
        No payment required to submit. We will respond with a free written diagnostic.
      </p>
    </form>
  );
}
